import { Component, OnInit, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AuthService, UserInfo } from '../../../core/services/auth.service';
import { ThemeService, ThemeType } from '../../../core/services/theme.service';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule],
  template: `
    <header class="header">
      <div class="header-left">
        <button class="menu-toggle" (click)="toggleSidebar()">☰</button>
      </div>
      <div class="header-right">
        <!-- Entity Selector -->
        <div class="theme-selector">
          <button class="theme-btn themed" (click)="toggleThemeMenu()" [class.active]="showThemeMenu">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" width="18" height="18"><path stroke-linecap="round" stroke-linejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" /></svg>
            <span class="theme-text">Entity</span>
            <span class="dropdown-arrow">▼</span>
          </button>
          @if (showThemeMenu) {
            <div class="theme-dropdown">
              @for (theme of themeService.themes; track theme.name) {
                <button
                  class="theme-option"
                  [class.selected]="themeService.theme() === theme.name"
                  (click)="selectTheme(theme.name)">
                  <span class="option-icon" [style.color]="theme.color">
                    @if (theme.icon === 'bank') {
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" width="18" height="18"><path stroke-linecap="round" stroke-linejoin="round" d="M12 21v-8.25M15.75 21v-8.25M8.25 21v-8.25M3 9l9-6 9 6m-1.5 12V10.332A48.36 48.36 0 0 0 12 9.75c-2.551 0-5.056.2-7.5.582V21M3 21h18M12 6.75h.008v.008H12V6.75Z" /></svg>
                    } @else if (theme.icon === 'hexagon') {
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" width="18" height="18"><path stroke-linecap="round" stroke-linejoin="round" d="M21 7.5l-9-5.25L3 7.5m18 0l-9 5.25m9-5.25v9l-9 5.25M3 7.5l9 5.25M3 7.5v9l9 5.25m0-9v9" /></svg>
                    } @else if (theme.icon === 'building') {
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" width="18" height="18"><path stroke-linecap="round" stroke-linejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" /></svg>
                    }
                  </span>
                  <span class="option-text">{{ theme.displayName }}</span>
                  @if (themeService.theme() === theme.name) {
                    <span class="check-mark">✓</span>
                  }
                </button>
              }
            </div>
          }
        </div>

        <div class="user-menu">
          <span class="user-avatar">👤</span>
          <div class="user-info">
            <span class="user-name">{{ currentUser?.fullName || 'Admin' }}</span>
            <span class="user-role">{{ currentUser?.role || 'Administrator' }}</span>
          </div>
          <button class="logout-btn" (click)="logout()" title="Logout">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" width="18" height="18"><path stroke-linecap="round" stroke-linejoin="round" d="M8.25 9V5.25A2.25 2.25 0 0 1 10.5 3h6a2.25 2.25 0 0 1 2.25 2.25v13.5A2.25 2.25 0 0 1 16.5 21h-6a2.25 2.25 0 0 1-2.25-2.25V15m-3 0-3-3m0 0 3-3m-3 3H15" /></svg>
            Logout
          </button>
        </div>
      </div>
    </header>
  `,
  styles: [`
    .header {
      background: var(--header-bg);
      padding: 0.75rem 1.5rem;
      display: flex;
      justify-content: space-between;
      align-items: center;
      box-shadow: 0 1px 3px var(--header-shadow);
      position: sticky;
      top: 0;
      z-index: 100;
      transition: background 0.3s ease;
    }
    .header-right {
      display: flex;
      align-items: center;
      gap: 1rem;
    }
    .menu-toggle {
      display: none;
      background: none;
      border: none;
      font-size: 1.5rem;
      cursor: pointer;
      color: var(--header-text-muted);
    }
    /* Theme Selector Styles */
    .theme-selector {
      position: relative;
    }
    .theme-btn {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.5rem 1rem;
      background: var(--content-bg);
      border: 1px solid var(--table-border);
      border-radius: 0.5rem;
      cursor: pointer;
      font-size: 0.875rem;
      color: var(--text-primary);
      transition: all 0.2s;
    }
    .theme-btn.themed {
      background: var(--cui-primary, #1E458E);
      border-color: var(--cui-primary, #1E458E);
      color: white;
    }
    .theme-btn.themed:hover, .theme-btn.themed.active {
      background: var(--btn-primary-hover, #163a75);
      border-color: var(--btn-primary-hover, #163a75);
    }
    .theme-btn:hover, .theme-btn.active {
      background: var(--table-hover-bg);
      border-color: var(--cui-primary);
    }
    .theme-icon { font-size: 1rem; }
    .theme-text { font-weight: 500; }
    .dropdown-arrow { font-size: 0.625rem; opacity: 0.7; }
    .theme-dropdown {
      position: absolute;
      top: calc(100% + 0.5rem);
      right: 0;
      background: var(--card-bg);
      border: 1px solid var(--table-border);
      border-radius: 0.5rem;
      box-shadow: 0 4px 12px var(--card-shadow);
      min-width: 180px;
      z-index: 1000;
      overflow: hidden;
    }
    .theme-option {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      width: 100%;
      padding: 0.75rem 1rem;
      background: none;
      border: none;
      cursor: pointer;
      font-size: 0.875rem;
      color: var(--text-primary);
      text-align: left;
      transition: background 0.2s;
    }
    .theme-option:hover {
      background: var(--table-hover-bg);
    }
    .theme-option.selected {
      background: var(--table-hover-bg);
      color: var(--cui-primary);
      font-weight: 500;
    }
    .option-icon { display: flex; align-items: center; justify-content: center; width: 20px; height: 20px; }
    .option-icon svg { width: 18px; height: 18px; }
    .check-mark { margin-left: auto; color: var(--cui-primary); font-weight: 600; }
    /* User Menu Styles */
    .user-menu {
      display: flex;
      align-items: center;
      gap: 0.75rem;
    }
    .user-avatar {
      font-size: 1.5rem;
    }
    .user-info {
      display: flex;
      flex-direction: column;
    }
    .user-name {
      font-weight: 600;
      font-size: 0.875rem;
      color: var(--header-text);
    }
    .user-role {
      font-size: 0.75rem;
      color: var(--header-text-muted);
    }
    .logout-btn {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      background: var(--cui-primary, #1E458E);
      color: white;
      border: none;
      padding: 0.5rem 1rem;
      border-radius: 0.375rem;
      cursor: pointer;
      font-size: 0.875rem;
      font-weight: 500;
      margin-left: 1rem;
      transition: background 0.2s;
    }
    .logout-btn:hover {
      background: var(--btn-primary-hover, #163a75);
    }
    @media (max-width: 991px) {
      .menu-toggle { display: block; }
    }
  `]
})
export class HeaderComponent implements OnInit {
  currentUser: UserInfo | null = null;
  showThemeMenu = false;

  currentThemeIcon = computed(() => {
    const theme = this.themeService.theme();
    return this.themeService.getThemeConfig(theme)?.icon || '🎨';
  });

  constructor(
    private authService: AuthService,
    public themeService: ThemeService
  ) {}

  ngOnInit(): void {
    this.authService.currentUser$.subscribe(user => {
      this.currentUser = user;
    });
  }

  toggleSidebar(): void {
    // Toggle sidebar visibility for mobile
  }

  toggleThemeMenu(): void {
    this.showThemeMenu = !this.showThemeMenu;
  }

  selectTheme(theme: string): void {
    this.themeService.setTheme(theme as any);
    this.showThemeMenu = false;
  }

  logout(): void {
    this.authService.logout();
  }
}

